import classNames from 'classnames';

import { Tag } from '@/core/tamagoshi/Tag/Tag';
import { Checkbox } from '@/domains/core/tamagoshiv2/components/Checkbox/Checkbox';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { ProductTagsColor } from '@/domains/productDiscovery/Listings/components/ProductCardListingsEnhancer/components/ProductCardListings/components/ProductTags/ProductTags';
import { type ProductCardTag } from '@/domains/productDiscovery/Listings/components/ProductCardListingsEnhancer/components/ProductCardListings/interfaces';
import { useCustomFacet } from '@/domains/productDiscovery/Listings/facets/hooks/useCustomFacet';
import { useRefine } from '@/domains/productDiscovery/Listings/hooks/useRefine';
import { translations } from '@/domains/productDiscovery/ProductCard/translations';
import { useCategoryFamilyId } from '@/productDiscovery/Listings/pages/Category/hooks/useCategoryFamilyId';
import { useCategoryId } from '@/productDiscovery/Listings/pages/Category/hooks/useCategoryId';
import { useCategoryLevel } from '@/productDiscovery/Listings/pages/Category/hooks/useCategoryLevel';

import { FilterBlock } from '../FilterBlock';
import { advantagesFilterTitle } from './translations';

import styles from './AdvantagesFilter.module.scss';

interface Props {
  title: string;
  facetName: string;
  urlKey: string;
  tagName?: ProductCardTag;
  className?: string;
  position?: number;
  filterCount?: number;
}

export const AdvantagesFilter = ({
  title,
  facetName,
  urlKey,
  tagName,
  className,
  position,
  filterCount,
}: Props) => {
  const { tags } = translations;
  const categoryId = useCategoryId();
  const categoryLevel = useCategoryLevel();
  const categoryFamilyId = useCategoryFamilyId();
  const refine = useRefine();

  const { value: customFacetValue } = useCustomFacet(facetName);
  const filter = customFacetValue?.values.find(
    ({ name }) => name === `${facetName}.${urlKey}`,
  );

  const filterItem = {
    id: facetName,
    label: title,
    isRefined: !!filter?.isRefined,
    value: String(filter?.isRefined || false),
  };

  const handleChange = async () => {
    refine({
      [urlKey]: !filterItem.isRefined ? 1 : undefined,
    });

    const { generateFiltersTracking } = await import(
      '@/domains/productDiscovery/Listings/components/Filters/tracking/generateFiltersTracking'
    );

    Gtm.push(
      generateFiltersTracking({
        e_label: filterItem.value,
        filter_id: filterItem.id,
        filter_name: filterItem.id,
        filter_value: filterItem.value,
        filter_position: position,
        filter_count: filterCount,
        category_id: categoryId,
        category_level: categoryLevel,
        family_id: categoryFamilyId,
      }),
    );
  };

  return (
    <FilterBlock
      className={classNames(styles.root, className)}
      id="advantages"
      title={advantagesFilterTitle}
    >
      <Checkbox
        id={filterItem.id}
        className={styles.oldCheckbox}
        inputClassName={styles.inputOldCheckbox}
        isChecked={filterItem.isRefined}
        onClick={handleChange}
        primary={filterItem.label}
        secondary={
          tagName && (
            <Tag color={ProductTagsColor[tagName]} revamp as="span">
              {tags[tagName as keyof typeof tags]?.label}
            </Tag>
          )
        }
      />
    </FilterBlock>
  );
};

AdvantagesFilter.displayName = 'AdvantagesFilter';
